import { FLOW_EVENTS, type FlowEvent } from "@care-by-ella/contracts";

// TES-224 — the trigger-event catalog, sourced from contracts FLOW_EVENTS so
// the dropdowns can never drift from what the API actually fires. Shared by
// FlowEditor and JourneyEditor (journeys can be event-triggered too).
export const FLOW_EVENT_LABELS: Record<FlowEvent, string> = {
  first_consult_completed: "Efter första läkarbesöket",
  lab_results_ready: "När provsvar skickats till patienten",
  treatment_activated: "När behandlingen aktiverats",
  cycle_renewed: "När receptet förnyats",
  subscription_active: "När prenumerationen blivit aktiv",
  weight_logged: "När patienten loggat vikt",
  check_in_submitted: "När en check-in skickats in",
  intake_submitted: "När hälsodeklarationen skickats in",
  meeting_booked: "När patienten bokat ett läkarbesök",
  missed_check_in: "När en check-in uteblivit",
  missed_weight_log: "När viktloggning uteblivit",
  two_weekly_checkins_missed: "När två veckoincheckningar i rad uteblivit",
  phase_changed: "När patienten byter fas (kommande)",
  // TES-860 (Ö-205) — the doctor's decision to activate a weight-stabilisation
  // plan. Deliberately NOT the same thing as `phase_changed` directly above:
  // that one is a nightly calendar sweep with no clinician in the loop, and
  // Emelie 2026-09-22 ruled that the twelve-month boundary may never change
  // clinical phase on its own. CP-61 therefore triggers on this event only.
  // The two labels must stay tellable apart in the authoring dropdown — that
  // distinction is the whole point of Ö-205.
  stabilisation_plan_activated:
    "När läkaren aktiverat en viktstabiliseringsplan (CP-61)",
  // TES-998 (Ö-238) — no longer "the weight reached the goal": it fires when
  // she chooses to keep her weight, as an alias of maintenance_requested.
  weight_goal_reached:
    "När patienten valt att behålla vikten (äldre namn, samma som nedan)",
  goal_zone_candidate_detected:
    "När en vikt når målvikten i aktiv behandling (valet öppnas)",
  goal_zone_choice_recorded:
    "När patienten valt vid målvikten (behålla eller nytt mål)",
  goal_zone_choice_deferred: "När patienten valt att fundera vid målvikten",
  goal_zone_candidate_withdrawn:
    "När vikten som öppnade valet rättats över målvikten",
  maintenance_requested: "När patienten valt att behålla vikten (CP-60)",
  patient_withdraws_maintenance_request:
    "När patienten satt ett nytt viktmål i stället (CP-60 avslutas)",
  maintenance_request_declined:
    "När läkaren valt fortsatt aktiv behandling i stället (CP-60 avslutas)",
  meeting_paid: "När besöksavgiften betalats",
  doctor_next_step_set: "När läkaren dokumenterat besöket",
  // TES-739 — CP-05: the video session has ended (verified), before the
  // documentation. `doctor_visit_documented` is the Drive name for the same
  // fact as doctor_next_step_set.
  doctor_visit_session_ended: "När vårdmötets videosession avslutats",
  doctor_visit_documented:
    "När läkaren dokumenterat besöket (Drive-namn, samma som ovan)",
  meeting_cancelled: "När vårdmötet avbokats",
  lab_feedback_approved: "När läkaren skickat provsvarsfeedback",
  coach_feedback_approved: "När coachens återkoppling godkänts och skickats",
  milestone_submitted: "När en månadsuppföljning skickats in",
  monthly_review_approved: "När läkaren godkänt månadsgenomgången",
  monthly_medical_review_published:
    "När läkarens månadsåterkoppling är publicerad till patienten",
  medication_pickup_required:
    "När ett nytt recept är utfärdat och patienten ska hämta ut läkemedlet",
  medication_pickup_reported:
    "När patienten bockat av att hon hämtat ut läkemedlet",
  body_measurement_submitted: "När kroppsmått skickats in",
  f01_submitted: "När Ellas första frågor besvarats",
  f01_booking_intent: "När patienten valt att boka efter ett tidigt stopp",
  treatment_decision_approved: "När behandling godkänts (CP-09A)",
  treatment_decision_rejected: "När behandling inte godkänts (CP-09B)",
  treatment_decision_completion_required:
    "När komplettering krävs före beslut (CP-09C)",
  // TES-537 — the doctor's "Nästa steg efter vårdmötet", one event per
  // CP-06 variant. "Kan inte gå vidare" triggers only CP-09B.
  next_step_upload_existing_labs:
    "När befintliga provsvar ska laddas upp (CP-06 + blodtryck/puls)",
  next_step_new_blood_test: "När nytt blodprov krävs (CP-06 + blodtryck/puls)",
  next_step_labs_verified:
    "När provsvar redan är verifierade i betrodd källa (CP-06 bara blodtryck/puls)",
  signed_up: "När patienten skapat sitt konto",
  // The actual first dose, reported by the patient — not the same thing as the
  // treatment being activated in our system. DT-01 hangs on this and nothing
  // else. A prescription transfer never triggers it: the patient is already
  // under way.
  first_dose_reported: "När patienten tagit sin första dos",
  // TES-1160 — her answer on a planned weekly dose day (taken or not taken).
  dose_reported: "När patienten registrerat veckans dos (tagen eller inte tagen)",
  vitals_recorded:
    "När patienten registrerat blodtryck och vilopuls (Din hälsa över tid)",
  // Ö-128 — all required evidence in the patient's CP-06 track is complete.
  // Track-independent on purpose: lab_results_ready covers only CP-06B, so a
  // patient who uploads existing lab results or has them verified had no
  // signal.
  medical_evidence_completed:
    "När allt underlag inför behandlingsbeslutet är klart (alla CP-06-spår)",
  // TES-720 — the CP-06 document's name for the same moment; fired in parallel.
  patient_evidence_tasks_completed:
    "När patientens uppgifter i CP-06 är klara, inkl. betalt blodprov (samma ögonblick som ovan, dokumentets namn)",
  // TES-729 — the prescription transfer.
  prescription_transfer_submitted:
    "När receptövertagets underlag skickats in till granskning",
  prescription_transfer_resubmitted:
    "När en begärd komplettering av receptövertaget skickats in",
  prescription_transfer_more_information_requested:
    "När läkaren begärt komplettering av receptövertaget",
  prescription_transfer_labs_requested:
    "När läkaren begärt nytt blodprov i receptövertaget",
  prescription_transfer_visit_required:
    "När läkaren kräver vårdmöte före beslut om receptövertag",
  prescription_transfer_approved: "När receptövertaget godkänts",
  prescription_transfer_cannot_be_taken_over:
    "När Ella inte kan ta över behandlingen",
};

export const TRIGGER_EVENTS: { key: FlowEvent; label: string }[] =
  FLOW_EVENTS.map((key) => ({ key, label: FLOW_EVENT_LABELS[key] }));
